import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
	Box,
	Typography,
	IconButton,
	Divider,
	TextField,
	Select,
	MenuItem,
	InputLabel,
	FormControl,
	RadioGroup,
	FormControlLabel,
	Radio,
	SelectChangeEvent,
} from '@mui/material';

import React, { useCallback, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { createTask } from 'common/api';
import { ReactComponent as CircleDeleteCheckFill } from 'common/assets/images/circle-check-delete-fill.svg';
import { ReactComponent as CircleCheckFill } from 'common/assets/images/circle-check-fill.svg';
import { ReactComponent as CircleCheck } from 'common/assets/images/circle-check.svg';
import { ReactComponent as CircleDeleteCheck } from 'common/assets/images/circle-delete-fill.svg';
import { ReactComponent as Send } from 'common/assets/images/sendApproval.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import TextFieldWithLabel from 'common/components/TextFieldWithLabel/TextFieldWithLabel';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { MODULES } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { ACTION_TYPE } from 'common/constants/TaxonomyConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { getRelativeTime, toSentenceCase } from 'common/utils/utilFunctions';

import { updateMyChanges } from 'features/HomePage/api';

import {
	ChangeItemProps,
	IApprovalIconsProps,
	IApproveViewProps,
	ACTION_VALUES,
	REJECT_OPTION_VALUES,
	OPTION_VALUES,
	SAVE_SUCCESS_MESSAGE,
	SAVE_FAILURE_MESSAGE,
	STATUS,
	OBJECT_TYPE,
	TASK_TYPE,
	CANNOT_SAVE_MESSAGE,
} from '../MyApprovalUtils';

import styles from './MyApprovalsChangesTab.module.scss';

const { OK } = HTTP_STATUS_CODES;

const { APPROVE, REJECT } = ACTION_VALUES;
const { REJECT_CHANGES, REQUEST_CHANGES } = REJECT_OPTION_VALUES;
const {
	APPROVE_VALUES,
	REJECT_VALUES,
	REQUEST_CHANGES_VALUES,
	CHANGE_REQUESTED,
} = OPTION_VALUES;
const { RISK_REFERENCE_TAXONOMY } = MODULES;

const { astrerickcolor } = styles;

const RenderApproveView = (props: IApproveViewProps) => {
	const { comment, reviewer, timestamp, status, priority, dueDate } = props;

	return (
		<Box
			ml={10.5}
			mt={2}
			p={1.5}
			className={styles['approve-container-styles']}
		>
			<div className={styles['review-comments']}>
				<Typography variant="body1">{`${status}:`}</Typography>
				<Typography variant="body1" fontStyle="italic" color="textSecondary">
					{comment}
				</Typography>
			</div>
			{priority && dueDate && (
				<Typography variant="inherit" color="textSecondary">
					{`Priority: ${priority}`} • {`Due Date: ${dueDate.split('T')[0]}`}
				</Typography>
			)}
			<Typography variant="caption" color="textSecondary">
				{getRelativeTime(timestamp)} • {reviewer}
			</Typography>
		</Box>
	);
};

const ApprovalIcons = (props: IApprovalIconsProps) => {
	const {
		isApproved,
		isRejected,
		oldContent,
		newContent,
		timestamp,
		user,
		handleApprove,
		handleReject,
		title,
		actionType,
		shouldAbleToChangeApproval,
		loggedInUser,
		requestedUser,
	} = props;

	return (
		<>
			<Box className={styles['changes-tab-container']}>
				<div className={styles['icon-buttons']}>
					<IconButton
						disabled={
							shouldAbleToChangeApproval || loggedInUser === requestedUser
						}
						color={isApproved ? 'primary' : 'default'}
						size="small"
						onClick={handleApprove}
						data-testid="approve-btn-click"
						disableRipple
					>
						{isApproved ? (
							<CircleCheckFill />
						) : (
							<CircleCheck className={styles['circle-check-styles']} />
						)}
					</IconButton>
					<IconButton
						color={isRejected ? 'primary' : 'default'}
						size="small"
						disabled={
							shouldAbleToChangeApproval || loggedInUser === requestedUser
						}
						onClick={handleReject}
						data-testid="reject-btn-click"
						disableRipple
					>
						{isRejected ? (
							<CircleDeleteCheckFill />
						) : (
							<CircleDeleteCheck className={styles['circle-check-styles']} />
						)}
					</IconButton>
				</div>
				<Typography className={styles['old-content-container-styles']}>
					<Typography
						variant="subtitle1"
						className={styles['title-styles']}
						component="span"
					>
						{`${toSentenceCase(title)} ${actionType.toLowerCase()}: `}
					</Typography>
					{oldContent && (
						<Typography
							className={styles['old-content-styles']}
							component="span"
						>
							{oldContent}
						</Typography>
					)}
					<Typography component="span">{newContent}</Typography>
				</Typography>
			</Box>
			<Box className={styles['time-styles']}>
				<Typography variant="caption" color="textSecondary">
					{getRelativeTime(timestamp)} • {user}
				</Typography>
			</Box>
		</>
	);
};

const ChangeItem: React.FC<ChangeItemProps> = (props: ChangeItemProps) => {
	const {
		entityId,
		entityName,
		entityType,
		changeObject,
		isChangesSubmitted,
		isApprovedChanges,
		submitChangesHandler,
		shouldAbleToChangeApproval,
	} = props;

	const {
		propertyName,
		actionType,
		oldValue,
		newValue,
		requestTime,
		requestBy,
		id,
		comments,
		requestByID,
	} = changeObject;

	const title = propertyName;
	const oldContent = oldValue;
	const newContent = newValue;
	const timestamp = requestTime;
	const user = requestBy;

	const dispatch = useDispatch();

	const [comment, setComment] = useState<string[]>(
		comments.map((res) => res.comments),
	);
	const [dueDate, setDueDate] = useState<string[]>(
		comments.map((res) => res.dueDate),
	);
	const { ADDED_ACTION, REMOVED_ACTION } = ACTION_TYPE;
	const [rejectOption, setRejectOption] = useState<
		REJECT_OPTION_VALUES.REQUEST_CHANGES | REJECT_OPTION_VALUES.REJECT_CHANGES
	>(
		actionType === ADDED_ACTION || actionType === REMOVED_ACTION
			? REJECT_OPTION_VALUES.REJECT_CHANGES
			: REJECT_OPTION_VALUES.REQUEST_CHANGES,
	);

	const [isSubmitted, setIsSubmitted] = useState<boolean>(isChangesSubmitted);

	const [isApproved, setIsApproved] = useState<boolean>(
		(isApprovedChanges && isSubmitted && shouldAbleToChangeApproval) || false,
	);
	const [isRejected, setIsRejected] = useState<boolean>(
		(!isApprovedChanges && isSubmitted && shouldAbleToChangeApproval) || false,
	);

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const [dataType, setDataType] = useState<string[]>(
		comments.map((res) => res.priority),
	);

	const [currentDataType, setCurrentDataType] = useState<string>('');
	const [currentDueDate, setCurrentDueDate] = useState<string>('');
	const [currentComment, setCurrentComment] = useState<string>('');

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const getActionType = () => {
		if (!shouldAbleToChangeApproval) {
			return '';
		} else if (isChangesSubmitted && isApproved) {
			return APPROVE;
		} else if (isChangesSubmitted && !isApproved) {
			return REJECT;
		}
		return '';
	};

	const [action, setAction] = useState<
		ACTION_VALUES.APPROVE | ACTION_VALUES.REJECT | ''
	>(getActionType());

	const handleCommentChange = (event: any) => {
		setComment([event.target.value]);
		setCurrentComment(event.target.value);
	};

	const handleApprove = () => {
		const userId = loggedInUser?.userId || '';
		if (userId === requestByID) {
			dispatch(
				setToastMessage({
					isToastOpen: true,
					message: CANNOT_SAVE_MESSAGE,
					severity: 'error',
				}),
			);
		} else {
			setComment(['']);
			setCurrentComment('');
			setAction(APPROVE);
			setIsApproved(true);
			setIsRejected(false);
		}
	};

	const handleReject = () => {
		const userId = loggedInUser?.userId || '';
		if (userId === requestByID) {
			dispatch(
				setToastMessage({
					isToastOpen: true,
					message: CANNOT_SAVE_MESSAGE,
					severity: 'error',
				}),
			);
		} else {
			setComment(['']);
			setCurrentComment('');
			setAction(REJECT);
			setIsRejected(true);
			setIsApproved(false);
		}
	};

	const handleSubmit = () => {
		handleSubmitApiCall();
	};

	const getStatusName = (status: string, flag?: boolean) => {
		switch (status) {
			case APPROVE:
				return APPROVE_VALUES;
			case REJECT:
				return REJECT_VALUES;
			case REQUEST_CHANGES:
				return flag ? CHANGE_REQUESTED : REQUEST_CHANGES_VALUES;
			default:
				return '';
		}
	};

	const getActionName = (flag?: boolean) => {
		if (action === REJECT && rejectOption === REJECT_CHANGES) {
			return getStatusName(REJECT);
		} else if (action === REJECT && rejectOption === REQUEST_CHANGES) {
			return getStatusName(REQUEST_CHANGES, flag);
		}
		return getStatusName(APPROVE);
	};

	const getDueDate = (dateValue: string) => {
		const dueDateUpdated: Date = new Date(dateValue);
		if (Number.isNaN(dueDateUpdated.getTime())) {
			return '';
		}
		return dueDateUpdated.toISOString();
	};

	const isRequestChangesSelected =
		action === REJECT && rejectOption === REQUEST_CHANGES;

	const handleSubmitApiCall = async () => {
		const payload = {
			reviewStatus: getActionName(),
			comments: comment?.at(0) || '',
			priority: isRequestChangesSelected ? dataType?.at(0) || '' : '',
			dueDate: isRequestChangesSelected ? getDueDate(dueDate?.at(0) || '') : '',
			auditId: id,
			entityName,
			requestedBy: `${loggedInUser?.firstName} ${loggedInUser?.lastName}`,
			email: loggedInUser?.userName || '',
		};
		const response = await updateMyChanges(payload);
		if (response.statusCode === OK) {
			setIsSubmitted(true);
			submitChangesHandler(
				action,
				id,
				true,
				comment?.at(0) || '',
				isRequestChangesSelected ? dueDate?.at(0) || '' : '',
				isRequestChangesSelected ? dataType?.at(0) || '' : '',
				getActionName(true),
				new Date().toISOString(),
				`${loggedInUser?.firstName} ${loggedInUser?.lastName}`,
				true,
			);
			updateTaskStatus(action, rejectOption);
		} else {
			dispatch(
				setToastMessage({
					message: SAVE_FAILURE_MESSAGE,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				isToastOpen: false,
				message: '',
				severity: '',
			}),
		);
	}, []);

	const updateTaskStatus = async (action: string, rejectOption: string) => {
		if (!(action === REJECT && rejectOption === REQUEST_CHANGES)) {
			return;
		}
		const payload = [
			{
				id: '',
				taskName: entityName,
				taskDescription: comment?.at(0),
				controlDescription: '',
				startDate: new Date().toISOString().split('T')[0],
				dueDate: new Date(dueDate?.at(0) || '').toISOString(),
				priorityLevel: dataType?.at(0),
				taskOwner: loggedInUser?.userId || '',
				assignedUserId: requestByID,
				status: STATUS,
				relatedParentObjectType: entityType,
				relatedParentObjectId: entityId,
				assignedObjectType: OBJECT_TYPE,
				assignedObjectId: id,
				createdBy: loggedInUser?.userId,
				taskType: TASK_TYPE,
				moduleName: RISK_REFERENCE_TAXONOMY,
			},
		];
		const response = await createTask(payload);
		if (response.statusCode === OK) {
			dispatch(
				setToastMessage({
					message: SAVE_SUCCESS_MESSAGE,
					severity: 'success',
					isToastOpen: true,
				}),
			);
		} else {
			dispatch(
				setToastMessage({
					message: SAVE_FAILURE_MESSAGE,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const handleDataTypeChange = useCallback((e: SelectChangeEvent) => {
		setDataType([e.target.value]);
		setCurrentDataType(e.target.value);
	}, []);

	const handleDueDateChange = useCallback((e: any) => {
		setDueDate([e.target.value]);
		setCurrentDueDate(e.target.value);
	}, []);

	const isAcceptSendEnabled = action === APPROVE && currentComment;

	const isRejectSendEnabled = action === REJECT && currentComment;

	const isRequestChangeButtonEnabled =
		currentComment && currentDataType && currentDueDate;

	const handleRejectOptionChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		setComment(['']);
		setCurrentComment('');
		setCurrentDataType('');
		setCurrentDueDate('');
		setRejectOption(
			(event.target as HTMLInputElement).value as
				| REJECT_OPTION_VALUES.REQUEST_CHANGES
				| REJECT_OPTION_VALUES.REJECT_CHANGES,
		);
	};

	const approveTextBox = () => (
		<Box ml={11} mt={3}>
			<TextFieldWithLabel
				type="text"
				size="small"
				showLabel
				isFilled
				label="Provide notes"
				fullWidth
				error={false}
				helperText=""
				rows={2}
				onChange={handleCommentChange}
				InputProps={{
					disableUnderline: true,
					inputProps: { 'data-testid': 'approve-input-text' },
					endAdornment: (
						<ButtonComp
							btnType="primary"
							disabled={!isAcceptSendEnabled}
							onClick={handleSubmit}
							compClass={styles['send-button']}
							data-testid="send-button"
						>
							<Box className={styles['send-button-icon']}>
								<Send />
							</Box>
						</ButtonComp>
					),
				}}
			/>
		</Box>
	);

	const rejectOptionView = () => (
		<Box mt={2} ml={11}>
			{!(actionType === ADDED_ACTION || actionType === REMOVED_ACTION) && (
				<FormControl component="fieldset">
					<RadioGroup
						row
						aria-label="reject-options"
						name="reject-options"
						value={rejectOption}
						onChange={handleRejectOptionChange}
					>
						<FormControlLabel
							value="requestChanges"
							control={<Radio />}
							data-testid="requestChanges"
							label="Request changes"
						/>
						<FormControlLabel
							value="reject"
							control={<Radio />}
							data-testid="reject"
							label="Reject"
						/>
					</RadioGroup>
				</FormControl>
			)}
			<TextFieldWithLabel
				type="text"
				isFilled
				size="small"
				error={false}
				helperText=""
				label="Provide reasoning for rejection"
				fullWidth
				rows={2}
				onChange={handleCommentChange}
				InputProps={{
					disableUnderline: true,
					inputProps: { 'data-testid': 'reject-input-text' },
					endAdornment: (
						<ButtonComp
							btnType="primary"
							disabled={!isRejectSendEnabled}
							onClick={handleSubmit}
							compClass={styles['send-button']}
							data-testid="send-button"
						>
							<Box className={styles['send-button-icon']}>
								<Send />
							</Box>
						</ButtonComp>
					),
				}}
			/>
		</Box>
	);

	const requestChangesView = () => (
		<Box mt={2} ml={11} gap={3}>
			<FormControl component="fieldset">
				<RadioGroup
					row
					aria-label="reject-options"
					name="reject-options"
					value={rejectOption}
					onChange={handleRejectOptionChange}
				>
					<FormControlLabel
						value="requestChanges"
						control={<Radio />}
						data-testid="requestChanges"
						label="Request changes"
					/>
					<FormControlLabel
						value="reject"
						control={<Radio />}
						data-testid="reject"
						label="Reject"
					/>
				</RadioGroup>
			</FormControl>
			<TextFieldWithLabel
				type="text"
				size="small"
				showLabel
				isFilled
				error={false}
				helperText=""
				label="Provide notes about changes"
				fullWidth
				rows={2}
				onChange={handleCommentChange}
				InputProps={{
					disableUnderline: true,
					inputProps: { 'data-testid': 'approve-input-text' },
				}}
			/>

			<Box display="flex" mt={2} alignItems="center" width="100%" gap={2}>
				<FormControl data-testid="form-control" sx={{ minWidth: '45%' }}>
					<InputLabel
						id="demo"
						sx={
							dataType.length
								? { marginTop: '10px' }
								: {
										'&.Mui-focused': {
											marginTop: '10px',
										},
								  }
						}
						className={styles['fileupload-select']}
					>
						Set priority level
						<span className={styles['fileupload-datatype-required']}> *</span>
					</InputLabel>
					<Select
						IconComponent={ExpandMoreRoundedIcon}
						labelId="demo"
						sx={{
							zIndex: 1300,
							width: '100%',
						}}
						onChange={handleDataTypeChange}
						inputProps={{
							'data-testid': 'datatype-ele',
						}}
					>
						<MenuItem value="High">{toSentenceCase('High')}</MenuItem>
						<MenuItem value="Medium">{toSentenceCase('Medium')}</MenuItem>
						<MenuItem value="Low">{toSentenceCase('Low')}</MenuItem>
					</Select>
				</FormControl>

				<TextField
					label="Due Date"
					type="date"
					required
					onChange={handleDueDateChange}
					InputProps={{
						inputProps: { 'data-testid': 'due-date' },
					}}
					InputLabelProps={{
						shrink: true,
						sx: {
							'.MuiInputLabel-asterisk': { color: astrerickcolor },
						},
					}}
					sx={{ minWidth: '45%' }}
				/>

				<ButtonComp
					btnType="primary"
					disabled={!isRequestChangeButtonEnabled}
					onClick={handleSubmit}
					compClass={styles['send-button']}
					data-testid="send-button"
					sx={{
						padding: '6px 12px',
						minWidth: '20%',
					}}
				>
					<Box className={styles['send-button-icon']}>
						<Send />
					</Box>
				</ButtonComp>
			</Box>
		</Box>
	);

	const renderApprovalView = () => (
		<div>
			{comments.map((res) => {
				const {
					commentsId,
					status,
					comments,
					createdDate,
					commentBy,
					priority,
					dueDate,
				} = res;
				return (
					<RenderApproveView
						key={commentsId}
						status={status}
						comment={comments}
						timestamp={createdDate}
						reviewer={commentBy}
						priority={priority}
						dueDate={dueDate}
					/>
				);
			})}
			{action === APPROVE && !shouldAbleToChangeApproval && approveTextBox()}
			{action === REJECT &&
				!shouldAbleToChangeApproval &&
				(rejectOption === REQUEST_CHANGES
					? requestChangesView()
					: rejectOptionView())}
		</div>
	);

	return (
		<Box sx={{ padding: 2 }} data-testid="changes-tab">
			<ApprovalIcons
				title={title}
				actionType={actionType}
				isApproved={isApproved}
				isRejected={isRejected}
				oldContent={oldContent}
				newContent={newContent}
				timestamp={timestamp}
				user={user}
				handleApprove={handleApprove}
				handleReject={handleReject}
				shouldAbleToChangeApproval={shouldAbleToChangeApproval}
				loggedInUser={loggedInUser?.email || ''}
				requestedUser={requestByID}
			/>
			{action === APPROVE &&
				(isSubmitted ? renderApprovalView() : approveTextBox())}
			{/* Reject View */}
			{action === REJECT &&
				(isSubmitted ? (
					renderApprovalView()
				) : (
					<Box mt={2}>
						<div>
							{rejectOption === REQUEST_CHANGES
								? requestChangesView()
								: rejectOptionView()}
						</div>
					</Box>
				))}
			<Divider sx={{ marginY: 2 }} />
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</Box>
	);
};

export default ChangeItem;
